import classNames from 'classnames';
import React, { FC, memo, useCallback, useRef } from 'react';
import { EllipsisProps } from '.';
import ResizeObserver from 'resize-observer-polyfill';
import useIsomorphicLayoutEffect from './hooks/useIsomorphicLayoutEffect';
import { getLineHeight } from './utils/compute';
import { getElementHeight, wrapTextChildNodesWithSpan } from './utils/dom';

const EllipsisMain: FC<EllipsisProps> = props => {
  const {
    className,
    ellipsis = true,
    content,
    dangerouslyUseInnerHTML = false,
    maxLine = 1,
    visibleLine,
    maxHeight,
    visibleHeight,
    ellipsisNode = '…',
    endExcludes = [],
    reflowOnResize = true,
    onReflow,
    onEllipsisClick
  } = props;
  // default visibleLine equal to maxLine.
  const _visibleLine = typeof visibleLine === 'undefined' ? maxLine : visibleLine;

  const observerRef = useRef<ResizeObserver | null>(null);
  const truncating = useRef(false);
  const ref = useRef<HTMLDivElement>(null);
  const textRef = useRef<HTMLSpanElement>(null);
  const ellipsisRef = useRef<HTMLSpanElement>(null);

  const lineHeightRef = useRef(0);

  const rePutellipsisNode = useCallback((textContainer: HTMLElement) => {
    const children = textContainer.childNodes;
    if (!children.length) return true;
    const nodes = [].slice.call(children);
    let i = nodes.length - 1;
    while (i >= 0) {
      const _node = nodes[i];
      if (!_node) continue;
      if (_node.nodeType === Node.TEXT_NODE) {
        _node.parentNode.appendChild(ellipsisRef.current);
        return true;
      }
      if (_node.childNodes?.length && _node?.textContent?.length > 0) {
        if (rePutellipsisNode(_node)) return true;
        break;
      }
      i--;
    }
  }, []);

  function handleOnReflow(ellipsis: boolean, result: string) {
    if (dangerouslyUseInnerHTML && ellipsis) {
      rePutellipsisNode(textRef.current);
    }
    if (onReflow && typeof onReflow === 'function') {
      onReflow(ellipsis, result);
    }
  }

  const reflow = useCallback(() => {
    if (!ref.current || !textRef.current || !ellipsisRef.current || truncating.current) {
      return;
    }
    ellipsisRef.current.style.display = 'none';
    if (dangerouslyUseInnerHTML) {
      textRef.current.innerHTML = content;
    } else {
      textRef.current.textContent = content;
    }
    if (!ellipsis) {
      return;
    }

    const max = typeof maxHeight === 'undefined' ? lineHeightRef.current * maxLine : maxHeight;
    // desired visible height of container.
    const visibleMax =
      typeof visibleHeight === 'undefined'
        ? typeof maxHeight === 'undefined'
          ? lineHeightRef.current * _visibleLine
          : maxHeight
        : visibleHeight;

    const height = getElementHeight(ref.current);
    // content will be truncated if the content's height bigger than Math.max(max, visibleMax).
    if (height <= Math.max(max, visibleMax)) {
      handleOnReflow(false, content);
      return;
    }

    truncating.current = true;
    ellipsisRef.current.style.display = 'inline';
    if (dangerouslyUseInnerHTML) {
      // wrap the text children node with span element.
      wrapTextChildNodesWithSpan(textRef.current);
      truncateHTML(ref.current, textRef.current, visibleMax);
    } else {
      truncateText(ref.current, textRef.current, visibleMax);
    }
    truncating.current = false;
  }, [
    content,
    ellipsis,
    dangerouslyUseInnerHTML,
    maxLine,
    visibleLine,
    maxHeight,
    visibleHeight,
    ellipsisNode,
    endExcludes
  ]);
  function truncateText(container: HTMLElement, textContainer: HTMLElement, max: number) {
    const text = textContainer.textContent || '';
    let currentText = '';
    let l = 0;
    let r = text.length;
    // Binary truncate text until get the max limit fragment of text.
    while (l < r) {
      const m = Math.floor((l + r) / 2);
      if (l === m) {
        break;
      }
      const temp = text.slice(l, m);
      const ellipsisText = ellipsisRef.current?.textContent ?? '';
      textContainer.textContent = currentText + temp + '...' + ellipsisText;
      const { height } = container.getBoundingClientRect();
      if (height > max) {
        r = m;
      } else {
        currentText += temp;
        l = m;
      }
    }
    // Remove the exclude char at the end of the content.
    while (endExcludes.includes(currentText[currentText.length - 1])) {
      currentText = currentText.slice(0, -1);
    }
    textContainer.textContent = currentText;
    // Callback after reflow.
    handleOnReflow(true, currentText);
  }
  function truncateHTML(container: HTMLElement, textContainer: HTMLElement, max: number) {
    // only enter this function when container overflow.
    const children = textContainer.childNodes;
    if (children.length === 1) {
      const node = children[0] as HTMLElement;
      if (node.nodeType === Node.TEXT_NODE) {
        truncateText(container, textContainer, max);
      } else {
        const html = node.innerHTML;
        // clear content to determine whether the empty node can be placed.
        node.innerHTML = '';
        const { height } = container.getBoundingClientRect();
        if (height > max) {
          // return after remove the node, if overflow with empty node.
          textContainer.removeChild(node);
          handleOnReflow(true, textContainer.innerHTML);
          return;
        }
        node.innerHTML = html;
        // recursive truncate node
        truncateHTML(container, node, max);
      }
    } else {
      const nodes = [].slice.call(children);
      textContainer.innerHTML = '';
      let i = 0;
      // find the critical node
      while (i < nodes.length) {
        const _node = nodes[i];
        textContainer.appendChild(_node);
        const { height } = container.getBoundingClientRect();
        if (height > max) {
          break;
        }
        i++;
      }

      const _textContainer = textContainer.childNodes[i];
      if (_textContainer && _textContainer.childNodes.length) {
        // truncate the critical node
        truncateHTML(container, textContainer.childNodes[i] as HTMLElement, max);
      } else {
        handleOnReflow(true, textContainer.innerHTML);
      }
    }
  }
  // Call truncate function to reflow when the main props change.
  useIsomorphicLayoutEffect(() => {
    if (ref.current) {
      lineHeightRef.current = getLineHeight(ref.current);
    }
    reflow();
  }, [reflow]);

  // Observe resize event of container if reflowOnResize is true.
  useIsomorphicLayoutEffect(() => {
    const dom = ref.current;
    if (dom && reflowOnResize) {
      if (!observerRef.current && ellipsis) {
        observerRef.current = new ResizeObserver(reflow);
        observerRef.current.observe(dom);
      }
    }
    return () => {
      if (observerRef.current && dom) {
        // Remove observer when component unmounted.
        observerRef.current?.unobserve(dom);
        observerRef.current = null;
      }
    };
  }, [ellipsis]);

  // callback of ellipsis click event
  const handleEllipsisClick = useCallback(
    event => {
      event.stopPropagation();
      if (onEllipsisClick && typeof onEllipsisClick === 'function') {
        onEllipsisClick();
      }
    },
    [onEllipsisClick]
  );

  return (
    <>
      <div ref={ref} className={classNames('__react-ellipsis-js', className)}>
        <span ref={textRef} className="__react-ellipsis-js-content" style={{ wordBreak: 'break-word' }}></span>
        <span ref={ellipsisRef} onClick={handleEllipsisClick}>
          {`...`}
          <span>{ellipsisNode}</span>
        </span>
      </div>
    </>
  );
};
export default memo(EllipsisMain);
